import React, { useState } from "react";
import { FaWhatsapp } from "react-icons/fa";
import { FiCheck, FiX, FiClock, FiChevronUp, FiChevronDown, FiLoader } from "react-icons/fi";
import { useBulkSend } from "./Bulksendcontext";

const StatusIcon = ({ status, countdown }) => {
    if (status === "success") return (
        <div className="w-6 h-6 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
            <FiCheck size={12} className="text-emerald-600" />
        </div>
    );
    if (status === "failed") return (
        <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
            <FiX size={12} className="text-red-500" />
        </div>
    );
    if (status === "sending") return (
        <div className="w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center flex-shrink-0">
            <div className="animate-spin rounded-full h-3 w-3 border-2 border-white border-t-transparent" />
        </div>
    );
    if (status === "waiting") return (
        <div className="w-6 h-6 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
            <span className="text-amber-600 font-bold text-xs leading-none">{countdown}s</span>
        </div>
    );
    // pending
    return (
        <div className="w-6 h-6 bg-slate-100 rounded-full flex items-center justify-center flex-shrink-0">
            <FiClock size={11} className="text-slate-400" />
        </div>
    );
};

// ── Main floating widget ──────────────────────────────────────────────────────
const BulkSendWidget = () => {
    const { session, clearSession } = useBulkSend();
    const [expanded, setExpanded] = useState(true);

    if (!session) return null;

    const { items, done } = session;
    const sent    = items.filter((i) => i.status === "success").length;
    const failed  = items.filter((i) => i.status === "failed").length;
    const total   = items.length;
    const pct     = total > 0 ? Math.round(((sent + failed) / total) * 100) : 0;
    const current = items.find((i) => i.status === "sending" || i.status === "waiting");

    return (
        <div
            className="fixed bottom-5 right-5 z-50 w-80 rounded-2xl shadow-2xl overflow-hidden"
            style={{ fontFamily: "ui-sans-serif, system-ui, sans-serif" }}
        >
            {/* ── Header ── */}
            <div className={`px-4 py-3 flex items-center justify-between gap-3 ${done ? "bg-slate-800" : "bg-gradient-to-r from-emerald-600 to-teal-600"}`}>
                <div className="flex items-center gap-2.5 min-w-0">
                    <div className={`flex-shrink-0 ${!done ? "animate-pulse" : ""}`}>
                        <FaWhatsapp size={18} className="text-white" />
                    </div>
                    <div className="min-w-0">
                        <p className="text-white text-sm font-semibold leading-tight truncate">
                            {done ? "Bulk Send Complete" : "Sending Badges…"}
                        </p>
                        <p className="text-white/70 text-xs leading-tight">
                            {sent} sent · {failed} failed · {total - sent - failed} remaining
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                        onClick={() => setExpanded((v) => !v)}
                        className="text-white/70 hover:text-white transition-colors p-1 rounded"
                    >
                        {expanded ? <FiChevronDown size={16} /> : <FiChevronUp size={16} />}
                    </button>
                    {done && (
                        <button
                            onClick={clearSession}
                            className="text-white/70 hover:text-white transition-colors p-1 rounded"
                        >
                            <FiX size={16} />
                        </button>
                    )}
                </div>
            </div>

            {/* ── Progress bar ── */}
            <div className="h-1.5 bg-slate-200">
                <div
                    className={`h-full transition-all duration-500 ease-out ${done ? "bg-slate-500" : "bg-emerald-500"}`}
                    style={{ width: `${pct}%` }}
                />
            </div>

            {/* ── Expanded list ── */}
            {expanded && (
                <div className="bg-white">
                    {/* Current item highlight (while running) */}
                    {!done && current && (
                        <div className="px-4 py-2.5 bg-emerald-50 border-b border-emerald-100 flex items-center gap-2.5">
                            <StatusIcon status={current.status} countdown={current.countdown} />
                            <div className="min-w-0 flex-1">
                                <p className="text-xs font-semibold text-slate-800 truncate">{current.name}</p>
                                <p className="text-xs text-slate-400 truncate">{current.phone}</p>
                            </div>
                            <span className="text-xs text-emerald-600 font-medium flex-shrink-0">
                                {current.status === "sending" ? "Sending…" : `Wait ${current.countdown}s`}
                            </span>
                        </div>
                    )}

                    {/* Scrollable full list */}
                    <div className="max-h-52 overflow-y-auto divide-y divide-slate-50">
                        {items.map((item, idx) => (
                            <div
                                key={item.badgeId}
                                className={`flex items-center gap-2.5 px-4 py-2 transition-colors
                                    ${item.status === "sending" ? "bg-emerald-50/60" : ""}
                                `}
                            >
                                <StatusIcon status={item.status} countdown={item.countdown} />
                                <div className="flex-1 min-w-0">
                                    <p className="text-xs font-medium text-slate-700 truncate">{item.name}</p>
                                    {item.status === "failed" && item.error && (
                                        <p className="text-xs text-red-400 truncate">{item.error}</p>
                                    )}
                                </div>
                                <span className="text-xs text-slate-300 flex-shrink-0">#{idx + 1}</span>
                            </div>
                        ))}
                    </div>

                    {/* Done footer */}
                    {done && (
                        <div className="px-4 py-3 bg-slate-50 border-t border-slate-100 flex items-center justify-between">
                            <div className="flex items-center gap-3 text-xs">
                                <span className="text-emerald-600 font-semibold">✓ {sent} sent</span>
                                {failed > 0 && <span className="text-red-500 font-semibold">✕ {failed} failed</span>}
                            </div>
                            <button
                                onClick={clearSession}
                                className="text-xs bg-slate-800 hover:bg-slate-700 text-white px-3 py-1.5 rounded-lg font-medium transition-colors"
                            >
                                Dismiss
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default BulkSendWidget;